// src/api/zkpa.api.ts
// ZKPA API functions — all key material sent as ciphertext only, never plaintext

import { api } from "@/shared/services/api";
import type {
  BootstrapKeyPayload,
  RecoverKeyPayload,
  MasterKeyStatusResponse,
  EncryptedMasterKeyResponse,
  UserPublicKeyResponse,
  ConnectionShare,
  CreateSharePayload,
  IntegrityReport,
  TaskKeyInitStatus,
} from "@/shared/types/zkpa.types";

// ── Key Management ──────────────────────────────────────────────────────────

export const zkpaApi = {
  /** Bootstrap — sends encMK+encSK+pubKey to server. Called once after seed phrase setup. */
  bootstrap: (payload: BootstrapKeyPayload) =>
    api.post("keys/bootstrap", { json: payload }).json<void>(),

  /**
   * Fetch ZKPA key status + encrypted key material for client-side decryption.
   *
   * Always returns HTTP 200 (never 404):
   *   - { bootstrapped: false } → user has not set up ZKPA yet
   *   - { bootstrapped: true, ...encMK } → key material ready for decryption
   */
  getEncryptedMasterKey: () =>
    api.get(`keys/my-master-key?_t=${Date.now()}`)
       .json<MasterKeyStatusResponse>(),

  /** Get any user's Ed25519 public key (for ECIES sharing) */
  getUserPublicKey: (userId: string) =>
    api.get(`users/${userId}/public-key`).json<UserPublicKeyResponse>(),

  /** Recover — sends new encMK derived from seed phrase */
  recover: (payload: RecoverKeyPayload) =>
    api.put("keys/recover", { json: payload }).json<void>(),

  /** Rotate — re-encrypt and update keys on server (used after seed-phrase recovery) */
  rotate: (payload: BootstrapKeyPayload) =>
    api.post("keys/rotate", { json: payload }).json<void>(),

  // ── Shares ──────────────────────────────────────────────────────────────────

  /** Create a P2P ECIES share (server stores ciphertext only, cannot decrypt CK) */
  createShare: (payload: CreateSharePayload) =>
    api.post("shares", { json: payload }).json<{ share_token: string }>(),

  /** List shares owned by current user, optionally filtered by serviceId */
  getMyShares: (serviceId?: string) =>
    api.get("shares", { searchParams: serviceId ? { service_id: serviceId } : {} })
       .json<ConnectionShare[]>(),

  /** List shares received by current user */
  getSharesReceived: () =>
    api.get("shares/received").json<ConnectionShare[]>(),

  /** Revoke a share (immediate — sweeper will enforce) */
  revokeShare: (shareToken: string) =>
    api.delete(`shares/${shareToken}`).json<void>(),

  /** Extend share TTL (max max_extensions times) */
  extendShare: (shareToken: string, expiresAt: string) =>
    api.put(`shares/${shareToken}/extend`, { json: { expires_at: expiresAt } }).json<void>(),

  // ── Integrity ────────────────────────────────────────────────────────────────

  /** Run bulk Ed25519 integrity check on server */
  bulkIntegrityCheck: (params: { dataType: string; since?: string }) =>
    api.get("integrity/check", {
      searchParams: {
        data_type: params.dataType,
        ...(params.since ? { since: params.since } : {}),
      },
    }).json<IntegrityReport>(),

  // ── Task Keys ────────────────────────────────────────────────────────────────

  /** Get task key reinit status (how many schedule keys need reloading) */
  getTaskKeyInitStatus: () =>
    api.get("tasks/schedules/key-init-status").json<TaskKeyInitStatus>(),

  /** Send decrypted Task Key to scheduler RAM (in-memory only, never persisted by server) */
  initTaskKey: (scheduleId: string, taskKeyBase64: string) =>
    api.post(`tasks/schedules/${scheduleId}/init-key`, {
      json: { task_key: taskKeyBase64 },
    }).json<void>(),
};
